// Central SEO configuration & helpers for Hopsy.
// Canonicals resolve against REACT_APP_SITE_URL (prod domain) so search engines
// attribute ranking to the real brand domain, not the preview URL.

export const SITE_URL =
  (process.env.REACT_APP_SITE_URL || "https://www.hayhopsy.com").replace(/\/$/, "");

export const SITE_NAME = "Hopsy";
export const SITE_TAGLINE = "Complete Care For Small Pets";
export const SITE_DEFAULT_DESCRIPTION =
  "Hopsy crafts premium, 100% sun-cured hay, pellets and bedding for rabbits, guinea pigs, hamsters and chinchillas in India. Vet-informed, low-dust, naturally nutritious.";
export const SITE_DEFAULT_IMAGE = `${SITE_URL}/hopsy-logo.png`;
export const SITE_LOCALE = "en_IN";
export const SITE_TWITTER = "@hayhopsy";

export const ORG_CONTACT = {
  legalName: "RSA Overseas Pvt Ltd",
  brand: "Hopsy",
  email: "hey@hayhopsy.com",
  whatsapp: ["+917822086946", "+918698881544"],
  website: "https://www.hayhopsy.com",
  country: "IN",
  gstin: "27AAJCR1114N1Z8",
  social: [
    "https://www.instagram.com/hayhopsy",
    "https://www.facebook.com/hayhopsy",
  ],
};

// Physical warehouse / registered business address — source of NAP truth.
export const BUSINESS_ADDRESS = {
  streetAddress: "Warehouse No 1 & 2, Khadkale",
  addressLocality: "Kamshet, Maval",
  addressRegion: "Maharashtra",
  postalCode: "410405",
  addressCountry: "IN",
};

export const formatAddressLine = () =>
  `${BUSINESS_ADDRESS.streetAddress}, ${BUSINESS_ADDRESS.addressLocality}, ${BUSINESS_ADDRESS.addressRegion} ${BUSINESS_ADDRESS.postalCode}, India`;

// Physical storefront hours (Mon–Sat 10:00–19:00 IST). Online store is 24/7.
export const OPENING_HOURS = [
  {
    dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"],
    opens: "10:00",
    closes: "19:00",
  },
];

export const canonical = (path = "/") => {
  const clean = path.startsWith("/") ? path : `/${path}`;
  return `${SITE_URL}${clean === "/" ? "" : clean}`;
};

export const abs = (url) => {
  if (!url) return SITE_DEFAULT_IMAGE;
  if (/^https?:\/\//i.test(url)) return url;
  return `${SITE_URL}${url.startsWith("/") ? url : `/${url}`}`;
};

// JSON-LD builders ---------------------------------------------------------

const _postalAddress = () => ({
  "@type": "PostalAddress",
  ...BUSINESS_ADDRESS,
});

export const orgSchema = () => ({
  "@context": "https://schema.org",
  "@type": "Organization",
  "@id": `${SITE_URL}/#organization`,
  name: SITE_NAME,
  alternateName: ORG_CONTACT.legalName,
  url: SITE_URL,
  logo: `${SITE_URL}/hopsy-logo.png`,
  email: ORG_CONTACT.email,
  address: _postalAddress(),
  vatID: ORG_CONTACT.gstin,
  taxID: ORG_CONTACT.gstin,
  sameAs: ORG_CONTACT.social,
  contactPoint: [
    {
      "@type": "ContactPoint",
      contactType: "customer service",
      email: ORG_CONTACT.email,
      telephone: ORG_CONTACT.whatsapp[0],
      areaServed: "IN",
      availableLanguage: ["English", "Hindi"],
    },
  ],
});

// LocalBusiness — subtype PetStore with OnlineStore as additional type. Google
// treats this as both a local "near me" candidate AND an online e-commerce entity.
export const localBusinessSchema = () => ({
  "@context": "https://schema.org",
  "@type": ["LocalBusiness", "PetStore", "OnlineStore"],
  "@id": `${SITE_URL}/#localbusiness`,
  name: `${SITE_NAME} — ${ORG_CONTACT.legalName}`,
  url: SITE_URL,
  image: `${SITE_URL}/hopsy-logo.png`,
  logo: `${SITE_URL}/hopsy-logo.png`,
  email: ORG_CONTACT.email,
  telephone: ORG_CONTACT.whatsapp[0],
  address: _postalAddress(),
  areaServed: { "@type": "Country", name: "India" },
  currenciesAccepted: "INR",
  paymentAccepted: "UPI, Credit Card, Debit Card, Net Banking, Wallet",
  priceRange: "₹₹",
  vatID: ORG_CONTACT.gstin,
  taxID: ORG_CONTACT.gstin,
  sameAs: ORG_CONTACT.social,
  openingHoursSpecification: OPENING_HOURS.map((h) => ({
    "@type": "OpeningHoursSpecification",
    dayOfWeek: h.dayOfWeek,
    opens: h.opens,
    closes: h.closes,
  })),
  // 24/7 online store availability — separate from physical storefront hours.
  hoursAvailable: [
    {
      "@type": "OpeningHoursSpecification",
      dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"],
      opens: "00:00",
      closes: "23:59",
      validFrom: "2024-01-01",
    },
  ],
});

export const websiteSchema = () => ({
  "@context": "https://schema.org",
  "@type": "WebSite",
  name: SITE_NAME,
  url: SITE_URL,
  inLanguage: "en-IN",
  potentialAction: {
    "@type": "SearchAction",
    target: `${SITE_URL}/shop?q={search_term_string}`,
    "query-input": "required name=search_term_string",
  },
});

export const breadcrumbSchema = (items) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: items.map((it, i) => ({
    "@type": "ListItem",
    position: i + 1,
    name: it.name,
    item: canonical(it.path),
  })),
});

export const productSchema = ({ product, rating }) => {
  const prices = (product.sizes || []).map((s) => s.price).filter(Boolean);
  const lowPrice = prices.length ? Math.min(...prices) : undefined;
  const highPrice = prices.length ? Math.max(...prices) : undefined;
  const schema = {
    "@context": "https://schema.org",
    "@type": "Product",
    name: product.name,
    description: product.seo_description || product.short_description || product.description,
    image: abs(product.image),
    brand: { "@type": "Brand", name: SITE_NAME },
    category: product.category,
    sku: product.slug,
    offers: {
      "@type": "AggregateOffer",
      priceCurrency: "INR",
      lowPrice,
      highPrice,
      offerCount: (product.sizes || []).length,
      availability: "https://schema.org/InStock",
      url: canonical(`/product/${product.slug}`),
    },
  };
  if (rating && rating.count > 0) {
    schema.aggregateRating = {
      "@type": "AggregateRating",
      ratingValue: rating.average.toFixed(1),
      reviewCount: rating.count,
      bestRating: 5,
      worstRating: 1,
    };
  }
  return schema;
};

export const faqSchema = (faqs) => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: (faqs || []).map((f) => ({
    "@type": "Question",
    name: f.q,
    acceptedAnswer: { "@type": "Answer", text: f.a },
  })),
});

export const articleSchema = ({ post }) => ({
  "@context": "https://schema.org",
  "@type": "Article",
  headline: post.title,
  description: post.excerpt,
  image: [abs(post.cover_image)],
  datePublished: post.published_at,
  dateModified: post.published_at,
  author: { "@type": "Organization", name: post.author || SITE_NAME },
  publisher: {
    "@type": "Organization",
    name: SITE_NAME,
    logo: { "@type": "ImageObject", url: `${SITE_URL}/hopsy-logo.png` },
  },
  mainEntityOfPage: { "@type": "WebPage", "@id": canonical(`/blog/${post.slug}`) },
  articleSection: post.category || post.tag,
  keywords: (post.keywords || []).join(", "),
});
